import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Grid, IconButton, TextField, Typography, MenuItem, Switch,
  Divider, CircularProgress, alpha, Chip, Tooltip, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
} from "@mui/material";
import {
  DragDropContext, Droppable, Draggable, DropResult,
} from "@hello-pangea/dnd";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import SearchIcon from "@mui/icons-material/Search";
import TimerIcon from "@mui/icons-material/Timer";
import EditIcon from "@mui/icons-material/Edit";
import {
  useGetPlaylistGroupQuery,
  useUpdatePlaylistGroupMutation,
  useGetPlaylistsQuery,
  useAddPlaylistToGroupMutation,
  useUpdatePlaylistGroupItemMutation,
  useApplyGroupDefaultTransitionMutation,
  useRemovePlaylistFromGroupMutation,
  useReorderPlaylistGroupItemsMutation,
  useGetCompanyQuery,
} from "../../../Api/attoDeskApi";
import AutoAwesomeMotionIcon from "@mui/icons-material/AutoAwesomeMotion";
import TransitionEditorPopover, { TransitionDraft } from "../../Playlist/Edit/TransitionEditorPopover";
import {
  GROUP_TRANSITIONS, EASINGS, DEFAULT_DURATION_MS, DEFAULT_EASING,
  transitionLabel,
} from "../../Player/transitions";
import {
  IPlaylist, IPlaylistGroup, IPlaylistGroupItem,
} from "../../../Api/Interface/api.interface";
import PageLoader from "../../../Components/Loader/PageLoader";
import { useNotifier } from "../../../Core/Notifier";
import {
  NAVY, NAVY_DARK, GOLD, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";
import SignageSection, {
  signageLabelSx, signageInputSx,
} from "../../../Components/ui/SignageSection";
import CreatePlaylistDialog from "../../../Components/Playlist/CreatePlaylistDialog";
import PlaylistEdit from "../../Playlist/Edit";
import { useAppSelector } from "../../../Store/hooks";
import {
  selectCanCreatePlaylist,
  selectCanEditPlaylist,
  selectIsSuperAdmin,
} from "../../../Store/Auth/AuthSelector";

const PLAYBACK_MODES = [
  { v: "SEQUENTIAL", label: "Sequential", help: "Play each playlist in order, then start over" },
  { v: "LOOP_ALL", label: "Loop All", help: "Continuously loop through every playlist" },
  { v: "RANDOM", label: "Random", help: "Shuffle the order on every cycle" },
];

const formatMinutes = (m: number): string => {
  if (!m || m <= 0) return "—";
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h > 0) return `${h}h ${r}m`;
  return `${r}m`;
};

const PlaylistGroupEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const groupId = Number(id);
  const { showMessage, showErrorMessage } = useNotifier();

  const { data, isLoading, refetch } = useGetPlaylistGroupQuery(groupId);
  const [updateGroup, { isLoading: isSaving }] = useUpdatePlaylistGroupMutation();
  const [addPlaylist] = useAddPlaylistToGroupMutation();
  const [updateItem] = useUpdatePlaylistGroupItemMutation();
  const [applyGroupDefaultTransition] = useApplyGroupDefaultTransitionMutation();
  const [removeItem] = useRemovePlaylistFromGroupMutation();
  const [reorder, { isLoading: isReordering }] = useReorderPlaylistGroupItemsMutation();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [playbackMode, setPlaybackMode] = useState("SEQUENTIAL");
  const [isActive, setIsActive] = useState(true);
  const [items, setItems] = useState<IPlaylistGroupItem[]>([]);
  const [companyId, setCompanyId] = useState<number | "">("");
  // One-shot hydration guard for the company selector. Later refetches of
  // the group (after add/remove/reorder/edit of a playlist, save failures,
  // modal open/close) must never clobber a company the operator has already
  // seen/selected but not yet saved. Requirements 5 & 6.
  const companyHydratedRef = useRef(false);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [createPlaylistOpen, setCreatePlaylistOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editPlaylistId, setEditPlaylistId] = useState<number | null>(null);

  // ── Group-level default playlist transition (part of Save Details).
  const [defaultTransition, setDefaultTransition] = useState<string>("cut");
  const [defaultTransitionDurationMs, setDefaultTransitionDurationMs] = useState<number>(DEFAULT_DURATION_MS);
  const [defaultTransitionEasing, setDefaultTransitionEasing] = useState<string>(DEFAULT_EASING);
  const defaultsHydratedRef = useRef(false);
  // Per-assignment transition popover.
  const [transitionAnchor, setTransitionAnchor] = useState<HTMLElement | null>(null);
  const [transitionItem, setTransitionItem] = useState<IPlaylistGroupItem | null>(null);

  const canCreatePlaylist = useAppSelector(selectCanCreatePlaylist);
  const canEditPlaylist = useAppSelector(selectCanEditPlaylist);
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);

  // Only super_admin needs the full companies list to (re)assign a group.
  // Tenant users see their assigned company as a locked value.
  const { data: companiesData } = useGetCompanyQuery(
    { searchText: "", id: "" } as any,
    { skip: !isSuperAdmin }
  );
  const companies: Array<{ id: number; companyName: string; is_active?: boolean }> =
    Array.isArray((companiesData as any)?.data)
      ? ((companiesData as any).data as Array<{ id: number; companyName: string; is_active?: boolean }>)
      : [];

  // Close the hosted playlist editor and pull fresh playlist data into the
  // assigned rows (name / items_count / duration_mode). Group form state lives
  // in this component and is never unmounted, so it is preserved as-is.
  const handleCloseEditPlaylist = () => {
    setEditPlaylistId(null);
    refetch();
  };

  const group = data?.data as unknown as IPlaylistGroup | undefined;

  // Seed the group meta fields once from the server. Later refetches (after
  // adding / removing / reordering / editing a playlist) refresh the assigned
  // rows via `items` without clobbering unsaved edits to name / description /
  // playback mode / active — those are only committed via "Save Details".
  const didInitMeta = useRef(false);
  useEffect(() => {
    if (group) {
      if (!didInitMeta.current) {
        setName(group.name);
        setDescription(group.description ?? "");
        setPlaybackMode(group.playback_mode);
        setIsActive(group.is_active);
        didInitMeta.current = true;
      }
      if (!defaultsHydratedRef.current) {
        setDefaultTransition(group.default_playlist_transition ?? "cut");
        setDefaultTransitionDurationMs(group.default_playlist_transition_duration_ms ?? DEFAULT_DURATION_MS);
        setDefaultTransitionEasing(group.default_playlist_transition_easing ?? DEFAULT_EASING);
        defaultsHydratedRef.current = true;
      }
      setItems(group.items ?? []);
      // Seed the company once. Primary source is the group's own
      // company_id. If the group has none (legacy row), fall back to the
      // company of its first assigned playlist as a safe default. This is
      // only used to populate the selector — it is NOT persisted until the
      // operator saves (requirement 8).
      if (!companyHydratedRef.current) {
        const legacyFromPlaylist = (group.items ?? [])
          .map((i) => i.playlist?.company_id)
          .find((cid): cid is number => cid != null);
        setCompanyId(group.company_id ?? legacyFromPlaylist ?? "");
        companyHydratedRef.current = true;
      }
    }
  }, [group]);

  // Ensure the assigned company can always be displayed even if it isn't in
  // the fetched super-admin list yet (race) or is soft-deleted/inactive.
  const companyOptions = useMemo(() => {
    const list = [...companies];
    const assigned = group?.company;
    if (assigned && !list.some((c) => c.id === assigned.id)) {
      list.push({
        id: assigned.id,
        companyName: assigned.name,
        is_active: assigned.is_active,
      });
    }
    return list;
  }, [companies, group?.company]);

  const selectedCompany = companyOptions.find((c) => c.id === companyId) ?? null;
  const showInactiveChip = selectedCompany != null && selectedCompany.is_active === false;

  const stats = useMemo(() => {
    const active = items.filter(i => i.is_active).length;
    const inactive = items.length - active;
    return { active, inactive, total: items.length };
  }, [items]);

  const handleSaveMeta = async () => {
    try {
      const body: any = {
        name: name.trim(),
        description: description.trim() || null,
        playback_mode: playbackMode,
        is_active: isActive,
      };
      // Persist the selected company. Super admins may reassign it; for a
      // legacy group that had no company, this is where the resolved
      // fallback is written to the database (requirement 8 & 15). The
      // backend ignores company_id for non-super-admins.
      if (companyId !== "") body.company_id = Number(companyId);
      // Group-level default playlist transition — part of Save Details.
      body.default_playlist_transition = defaultTransition;
      body.default_playlist_transition_duration_ms = defaultTransitionDurationMs;
      body.default_playlist_transition_easing = defaultTransitionEasing;
      const res = await updateGroup({ id: groupId, body }).unwrap();
      if (res.status) { showMessage("Saved"); refetch(); }
      else showErrorMessage((res as any).message || "Failed to save");
    } catch (err: any) { showErrorMessage(err?.data?.message || "Failed to save"); }
  };

  const patchLocalItem = (itemId: number, patch: Partial<IPlaylistGroupItem>) =>
    setItems(prev => prev.map(i => (i.id === itemId ? { ...i, ...patch } : i)));

  const handleSaveItemTransition = async (item: IPlaylistGroupItem, draft: TransitionDraft) => {
    const body = {
      transition_to_next: draft.transition_in ?? "cut",
      transition_duration_ms: draft.transition_duration_ms ?? DEFAULT_DURATION_MS,
      transition_easing: draft.transition_easing ?? DEFAULT_EASING,
      use_group_default_transition: draft.use_default ?? true,
    };
    patchLocalItem(item.id, body as Partial<IPlaylistGroupItem>);
    try {
      await updateItem({ groupId, itemId: item.id, body }).unwrap();
      showMessage("Transition saved");
      refetch();
    } catch { showErrorMessage("Failed to save transition"); refetch(); }
  };

  const handleApplyDefaultToAll = async () => {
    try {
      await applyGroupDefaultTransition({
        id: groupId,
        body: {
          default_playlist_transition: defaultTransition,
          default_playlist_transition_duration_ms: defaultTransitionDurationMs,
          default_playlist_transition_easing: defaultTransitionEasing,
        },
      }).unwrap();
      showMessage("Default transition applied to all assigned playlists");
      defaultsHydratedRef.current = false;
      refetch();
    } catch { showErrorMessage("Failed to apply to all playlists"); }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    const next = [...items];
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setItems(next);
    try {
      const res = await reorder({
        id: groupId,
        item_ids: next.map(i => i.id),
      }).unwrap();
      if (res.status) { showMessage("Order saved"); refetch(); }
    } catch {
      showErrorMessage("Failed to save order");
      refetch();
    }
  };

  const handleAdd = async (playlist: IPlaylist) => {
    if (isAdding) return;
    setIsAdding(true);
    try {
      // Backend appends new items to the end of the group's order.
      const res = await addPlaylist({
        id: groupId,
        playlist_id: playlist.id,
      }).unwrap();
      if (res.status) {
        showMessage(`Added "${playlist.name}"`);
        refetch();
      } else {
        showErrorMessage((res as any).message || "Failed to add");
      }
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to add");
    } finally {
      setIsAdding(false);
    }
  };

  // A brand-new playlist was created from inside the picker: close the create
  // dialog + picker, then append it to this group. Group form state is never
  // touched, so name/description/playback/active/order all stay intact.
  const handlePlaylistCreated = async (playlist: IPlaylist) => {
    // The create dialog already assigned the new playlist to this group via
    // playlist_group_id (see CreatePlaylistDialog below), which creates the
    // pivot row. Re-adding here would hit the unique (group, playlist)
    // constraint — so just refresh the group's Assigned Playlists list.
    setCreatePlaylistOpen(false);
    setPickerOpen(false);
    showMessage(`Added "${playlist.name}"`);
    refetch();
  };

  const handleToggleActive = async (item: IPlaylistGroupItem) => {
    try {
      const res = await updateItem({
        groupId,
        itemId: item.id,
        body: { is_active: !item.is_active },
      }).unwrap();
      if (res.status) { refetch(); }
    } catch { showErrorMessage("Failed to update"); }
  };

  const handleSetDuration = async (item: IPlaylistGroupItem, minutes: number | null) => {
    try {
      const res = await updateItem({
        groupId,
        itemId: item.id,
        body: { duration_minutes: minutes },
      }).unwrap();
      if (res.status) { refetch(); }
    } catch { showErrorMessage("Failed to update duration"); }
  };

  const handleRemove = async (itemId: number) => {
    try {
      const res = await removeItem({ groupId, itemId }).unwrap();
      if (res.status) { showMessage("Removed"); refetch(); }
    } catch { showErrorMessage("Failed to remove"); }
  };

  if (isLoading || !group) return <PageLoader height="20vh" />;

  return (
    <Box sx={{ bgcolor: "#f1f5f9", minHeight: "calc(100vh - 100px)", mx: -3, mt: -3 }}>
      {/* Header */}
      <Box sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
        px: 3, position: "sticky", top: 0, zIndex: 10,
        boxShadow: "0 4px 30px rgba(0,0,0,0.18)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
          <IconButton onClick={() => navigate("/playlist-groups/list")} size="small"
            sx={{
              border: "1px solid rgba(255,255,255,0.15)", borderRadius: 2,
              width: 38, height: 38, bgcolor: "rgba(255,255,255,0.05)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
            }}>
            <ArrowBackIcon sx={{ fontSize: 18, color: "#e2e8f0" }} />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
            boxShadow: "0 4px 12px rgba(124,58,237,0.45)",
          }}>
            <CollectionsBookmarkIcon sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Typography fontSize={20} fontWeight={800} color="#fff" letterSpacing="-0.02em"
                sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 320 }}>
                {name || "Untitled Group"}
              </Typography>
              <Chip size="small" label={group.playback_mode}
                sx={{
                  height: 22, fontSize: 11, fontWeight: 700,
                  bgcolor: "rgba(124,58,237,0.18)", color: "#c4b5fd",
                  border: "1px solid rgba(124,58,237,0.35)",
                }} />
              <Chip size="small"
                label={isActive ? "Active" : "Inactive"}
                sx={{
                  height: 22, fontSize: 11, fontWeight: 700,
                  bgcolor: isActive ? "rgba(16,185,129,0.18)" : "rgba(148,163,184,0.18)",
                  color: isActive ? "#a7f3d0" : "#cbd5e1",
                  border: `1px solid ${isActive ? "rgba(16,185,129,0.35)" : "rgba(148,163,184,0.3)"}`,
                }} />
            </Box>
            <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
              <Typography fontSize={11.5} color="rgba(255,255,255,0.6)">
                {stats.total} playlist{stats.total === 1 ? "" : "s"} · {stats.active} active
              </Typography>
              <Typography fontSize={11.5} color="rgba(255,255,255,0.6)">
                {group.devices_count ?? 0} device{(group.devices_count ?? 0) === 1 ? "" : "s"} assigned
              </Typography>
              {group.total_duration > 0 && (
                <Typography fontSize={11.5} color="rgba(255,255,255,0.6)">
                  Estimated runtime ~ {formatMinutes(group.total_duration)}
                </Typography>
              )}
            </Box>
          </Box>
          <Button variant="contained" disableElevation size="small"
            disabled={isSaving} onClick={handleSaveMeta}
            startIcon={isSaving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 700, px: 3,
              background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
              "&:hover": { background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)" },
            }}>
            {isSaving ? "Saving…" : "Save Details"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* LEFT: meta */}
          <Grid item xs={12} lg={4}>
            <SignageSection
              icon={<InfoOutlinedIcon sx={{ fontSize: 18, color: "#3b82f6" }} />}
              title="Group Information"
              accentColor="#3b82f6"
            >
              {/* Company selector. Editable for super_admin, locked for
                  tenant users (they still see the assignment). Seeded from
                  the group's company_id (with a legacy playlist fallback) —
                  never from name, never from companies[0], never hardcoded. */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                <Typography sx={{ ...signageLabelSx, mb: 0 }}>Company *</Typography>
                {showInactiveChip && (
                  <Chip label="Inactive" size="small"
                    sx={{
                      height: 18, fontSize: 9, fontWeight: 700,
                      bgcolor: "#fef2f2", color: "#b91c1c",
                      border: "1px solid #fecaca",
                    }} />
                )}
              </Box>
              <TextField select fullWidth size="small"
                value={companyId === "" ? "" : String(companyId)}
                onChange={(e) => {
                  const v = e.target.value;
                  setCompanyId(v === "" ? "" : Number(v));
                }}
                disabled={!isSuperAdmin}
                helperText={
                  !isSuperAdmin
                    ? "Only a super admin can reassign this group to a different company."
                    : undefined
                }
                sx={{
                  mb: 2, ...signageInputSx,
                  "& .MuiFormHelperText-root": { fontSize: 10, ml: 0, mt: 0.5 },
                }}>
                <MenuItem value="">— Select a company —</MenuItem>
                {companyOptions.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.companyName}
                    {c.is_active === false && (
                      <Typography component="span" sx={{ ml: 1, fontSize: 10, fontWeight: 700, color: "#b91c1c" }}>
                        · inactive
                      </Typography>
                    )}
                  </MenuItem>
                ))}
              </TextField>

              <Typography sx={signageLabelSx}>Group Name *</Typography>
              <TextField fullWidth value={name}
                onChange={(e) => setName(e.target.value)}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.5, fontSize: 15, fontWeight: 600, bgcolor: "#fff",
                    "& fieldset": { borderColor: "#e2e8f0" },
                    "&.Mui-focused fieldset": { borderColor: "#3b82f6", borderWidth: 2 },
                  },
                }} />
              <Typography sx={signageLabelSx}>Description</Typography>
              <TextField fullWidth size="small" multiline rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={{ mb: 2, ...signageInputSx }} />
            </SignageSection>

            <SignageSection
              icon={<SettingsIcon sx={{ fontSize: 18, color: "#7c3aed" }} />}
              title="Playback Settings"
              accentColor="#7c3aed"
            >
              <Typography sx={signageLabelSx}>Playback Mode</Typography>
              <TextField select fullWidth size="small"
                value={playbackMode}
                onChange={(e) => setPlaybackMode(e.target.value)}
                sx={{ mb: 2, ...signageInputSx }}>
                {PLAYBACK_MODES.map(m => (
                  <MenuItem key={m.v} value={m.v}>
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{m.label}</Typography>
                      <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{m.help}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>

              {/* Default playlist-to-playlist transition — inherited by
                  assignments that opt in. Part of Save Details. */}
              <Box sx={{
                p: 2, mb: 2, borderRadius: 2.5, bgcolor: "#faf5ff",
                border: `1px solid ${alpha("#7c3aed", 0.18)}`,
              }}>
                <Typography sx={{ ...signageLabelSx, color: "#6d28d9" }}>Default Playlist Transition</Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <TextField select size="small" label="Transition" value={defaultTransition}
                    onChange={(e) => setDefaultTransition(e.target.value)}
                    sx={{ flex: "1 1 45%", minWidth: 130, bgcolor: "#fff" }}>
                    {GROUP_TRANSITIONS.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField select size="small" label="Duration" value={String(defaultTransitionDurationMs)}
                    onChange={(e) => setDefaultTransitionDurationMs(Number(e.target.value))}
                    sx={{ flex: "1 1 45%", minWidth: 100, bgcolor: "#fff" }}>
                    {[200, 300, 500, 750, 1000, 1500, 2000, 3000].map((ms) => (
                      <MenuItem key={ms} value={String(ms)}>{ms / 1000}s</MenuItem>
                    ))}
                  </TextField>
                  <TextField select size="small" label="Easing" value={defaultTransitionEasing}
                    onChange={(e) => setDefaultTransitionEasing(e.target.value)}
                    sx={{ flex: "1 1 45%", minWidth: 130, bgcolor: "#fff" }}>
                    {EASINGS.map((e) => (
                      <MenuItem key={e.id} value={e.id}>{e.label}</MenuItem>
                    ))}
                  </TextField>
                  <Button size="small" variant="outlined"
                    onClick={handleApplyDefaultToAll}
                    sx={{ flex: "1 1 45%", minWidth: 130, textTransform: "none", borderColor: alpha("#7c3aed", 0.4), color: "#6d28d9" }}>
                    Apply to all assigned playlists
                  </Button>
                </Box>
              </Box>

              <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                p: 2, borderRadius: 2.5,
                bgcolor: isActive ? alpha("#10b981", 0.05) : "#f8fafc",
                border: `1px solid ${isActive ? alpha("#10b981", 0.2) : "#e2e8f0"}`,
              }}>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>Active</Typography>
                  <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                    Devices only receive active groups
                  </Typography>
                </Box>
                <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              </Box>
            </SignageSection>
          </Grid>

          {/* RIGHT: ordered playlists */}
          <Grid item xs={12} lg={8}>
            <SignageSection
              icon={<PlaylistPlayIcon sx={{ fontSize: 18, color: "#7c3aed" }} />}
              title={`Assigned Playlists (${stats.total})`}
              accentColor="#7c3aed"
              subtitle="Drag to reorder · click the eye to enable/disable · click the timer to set per-group duration"
              right={
                <Button variant="contained" disableElevation size="small"
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setPickerOpen(true)}
                  sx={{
                    borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700,
                    background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
                    "&:hover": { background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)" },
                  }}>
                  Add Playlist
                </Button>
              }
            >
              {items.length === 0 ? (
                <Box sx={{
                  py: 5, textAlign: "center",
                  border: `2px dashed ${BORDER}`, borderRadius: 3, bgcolor: "#f8fafc",
                }}>
                  <PlaylistPlayIcon sx={{ fontSize: 36, color: "#cbd5e1", mb: 1 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY, mb: 0.5 }}>
                    No playlists in this group yet
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mb: 2 }}>
                    Add playlists you've already built — the group plays them in order.
                  </Typography>
                  <Button variant="contained" disableElevation
                    startIcon={<AddIcon />}
                    onClick={() => setPickerOpen(true)}
                    sx={{
                      borderRadius: 2.5, textTransform: "none", fontWeight: 700,
                      background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
                    }}>
                    Add Playlist
                  </Button>
                </Box>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="group-items">
                    {(provided) => (
                      <Box ref={provided.innerRef} {...provided.droppableProps}>
                        {items.map((item, index) => (
                          <Draggable key={item.id} draggableId={String(item.id)} index={index}>
                            {(p, snap) => {
                              const pl = item.playlist;
                              const dim = !item.is_active;
                              return (
                                <Box ref={p.innerRef} {...p.draggableProps}
                                  sx={{
                                    display: "flex", alignItems: "center", gap: 2, p: 1.5, mb: 1.25,
                                    borderRadius: 2.5,
                                    bgcolor: snap.isDragging
                                      ? alpha("#7c3aed", 0.08)
                                      : (dim ? "#f8fafc" : "#fff"),
                                    border: `1px solid ${snap.isDragging ? alpha("#7c3aed", 0.4) : BORDER}`,
                                    boxShadow: snap.isDragging
                                      ? `0 12px 28px ${alpha("#7c3aed", 0.22)}`
                                      : "0 1px 2px rgba(11,30,45,0.04)",
                                    opacity: dim ? 0.65 : 1,
                                    transition: "all 0.15s ease",
                                  }}>
                                  <Box {...p.dragHandleProps}
                                    sx={{
                                      cursor: "grab", color: "#cbd5e1",
                                      "&:active": { cursor: "grabbing" },
                                      "&:hover": { color: "#7c3aed" },
                                    }}>
                                    <DragIndicatorIcon />
                                  </Box>
                                  <Box sx={{
                                    width: 36, height: 36, borderRadius: "10px",
                                    background: `linear-gradient(135deg, ${alpha("#7c3aed", 0.15)} 0%, ${alpha("#7c3aed", 0.05)} 100%)`,
                                    border: `1px solid ${alpha("#7c3aed", 0.2)}`,
                                    color: "#7c3aed",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: 13, fontWeight: 800,
                                  }}>
                                    {String(index + 1).padStart(2, "0")}
                                  </Box>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{
                                      fontSize: 14, fontWeight: 700, color: NAVY,
                                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                    }}>
                                      {pl?.name || "—"}
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 1.25, alignItems: "center", mt: 0.75, flexWrap: "wrap" }}>
                                      <Chip size="small" icon={<PermMediaIcon sx={{ fontSize: 12 }} />}
                                        label={`${pl?.items_count ?? 0} item${(pl?.items_count ?? 0) === 1 ? "" : "s"}`}
                                        sx={{
                                          height: 20, fontSize: 11, fontWeight: 700,
                                          bgcolor: "#dbeafe", color: "#1e40af",
                                          border: "1px solid #bfdbfe",
                                          "& .MuiChip-icon": { color: "#3b82f6", ml: 0.5 },
                                        }} />
                                      <Chip size="small"
                                        label={pl?.duration_mode || "PLAY_FULL"}
                                        sx={{
                                          height: 20, fontSize: 10, fontWeight: 700,
                                          bgcolor: "#eef2ff", color: "#4338ca",
                                          border: "1px solid #c7d2fe",
                                        }} />
                                      <Chip size="small" icon={<TimerIcon sx={{ fontSize: 11 }} />}
                                        label={item.duration_minutes
                                          ? `${item.duration_minutes} min`
                                          : (pl?.duration_minutes ? `${pl.duration_minutes} min` : "play full")}
                                        onClick={() => {
                                          const cur = item.duration_minutes ?? pl?.duration_minutes ?? null;
                                          const next = window.prompt(
                                            "Override duration (minutes). Leave blank to use playlist default.",
                                            cur ? String(cur) : ""
                                          );
                                          if (next === null) return;
                                          const n = next.trim() === "" ? null : Math.max(1, parseInt(next, 10) || 0);
                                          handleSetDuration(item, n);
                                        }}
                                        sx={{
                                          height: 20, fontSize: 11, fontWeight: 700, cursor: "pointer",
                                          bgcolor: "#fef3c7", color: "#92400e",
                                          border: "1px solid #fcd34d",
                                          "& .MuiChip-icon": { color: "#d97706", ml: 0.5 },
                                          "&:hover": { bgcolor: "#fde68a" },
                                        }} />
                                      {pl && !pl.is_active && (
                                        <Chip size="small" label="Playlist Inactive"
                                          sx={{
                                            height: 20, fontSize: 10, fontWeight: 700,
                                            bgcolor: "#fee2e2", color: "#b91c1c",
                                            border: "1px solid #fecaca",
                                          }} />
                                      )}
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: "flex", gap: 0.5 }}>
                                    <Tooltip title={item.is_active ? "Disable in playback" : "Enable in playback"}>
                                      <IconButton size="small" onClick={() => handleToggleActive(item)}
                                        sx={{
                                          color: item.is_active ? "#10b981" : "#94a3b8",
                                          "&:hover": { bgcolor: item.is_active ? "#ecfdf5" : "#f1f5f9" },
                                        }}>
                                        {item.is_active ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Open playlist">
                                      <IconButton size="small"
                                        onClick={() => pl && navigate(`/playlist/edit/${pl.id}`)}
                                        sx={{ color: "#3b82f6", "&:hover": { bgcolor: alpha("#3b82f6", 0.08) } }}>
                                        <PlaylistPlayIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    {canEditPlaylist && (
                                      <Tooltip title="Edit playlist">
                                        <IconButton size="small"
                                          // Stop propagation / prevent the drag handle from
                                          // picking up the interaction; edit the real playlist
                                          // in a dialog so the group form state is preserved.
                                          onMouseDown={(e) => e.stopPropagation()}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (pl?.id) setEditPlaylistId(pl.id);
                                            else showErrorMessage("This playlist is no longer available");
                                          }}
                                          sx={{ color: "#7c3aed", "&:hover": { bgcolor: alpha("#7c3aed", 0.08) } }}>
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title={
                                      (item.use_group_default_transition ?? true)
                                        ? "Transition to next playlist: group default"
                                        : `Transition to next playlist: ${transitionLabel(item.transition_to_next)}`
                                    }>
                                      <IconButton size="small"
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onClick={(e) => { e.stopPropagation(); setTransitionItem(item); setTransitionAnchor(e.currentTarget); }}
                                        sx={{
                                          color: (item.use_group_default_transition ?? true) ? "#94a3b8" : "#7c3aed",
                                          "&:hover": { bgcolor: alpha("#7c3aed", 0.08) },
                                        }}>
                                        <AutoAwesomeMotionIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Remove from group">
                                      <IconButton size="small" onClick={() => handleRemove(item.id)}
                                        sx={{ color: "#94a3b8", "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" } }}>
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              );
                            }}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
              {isReordering && (
                <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 1, textAlign: "right" }}>
                  Saving order…
                </Typography>
              )}
            </SignageSection>
          </Grid>
        </Grid>
      </Box>

      <TransitionEditorPopover
        open={!!transitionItem}
        anchorEl={transitionAnchor}
        mode="group"
        defaultLabel="Use group default"
        value={transitionItem ? {
          transition_in: transitionItem.transition_to_next ?? "cut",
          transition_duration_ms: transitionItem.transition_duration_ms ?? defaultTransitionDurationMs,
          transition_easing: transitionItem.transition_easing ?? defaultTransitionEasing,
          use_default: transitionItem.use_group_default_transition ?? true,
        } : {}}
        onSave={(draft) => { if (transitionItem) handleSaveItemTransition(transitionItem, draft); }}
        onClose={() => { setTransitionItem(null); setTransitionAnchor(null); }}
      />

      <AddPlaylistDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(p) => { handleAdd(p); setPickerOpen(false); }}
        excludeIds={new Set(items.map(i => i.playlist_id))}
        canCreatePlaylist={canCreatePlaylist}
        onCreateNew={() => setCreatePlaylistOpen(true)}
        isAdding={isAdding}
      />

      <CreatePlaylistDialog
        open={createPlaylistOpen}
        onClose={() => setCreatePlaylistOpen(false)}
        onCreated={handlePlaylistCreated}
        defaultCompanyId={companyId}
        // Opened from the Playlist Group workflow — preselect and lock this
        // group so the new playlist is created already assigned to it.
        defaultPlaylistGroupId={groupId}
        lockPlaylistGroup
      />

      {/* Full playlist editor hosted in a dialog — reuses the existing
          Playlist Edit page (same form / API / validation / permissions). The
          group page stays mounted underneath, so no group form data is lost. */}
      <Dialog
        open={editPlaylistId !== null}
        onClose={handleCloseEditPlaylist}
        fullScreen
        PaperProps={{ sx: { bgcolor: "#f1f5f9", overflowX: "hidden" } }}
      >
        {editPlaylistId !== null && (
          <PlaylistEdit
            playlistId={editPlaylistId}
            onExit={handleCloseEditPlaylist}
            embedded
            // Requirement 12: the hosted editor's Add Media flow prefers the
            // playlist's own company, but falls back to this group's company
            // when the playlist has none.
            fallbackCompanyId={companyId === "" ? undefined : Number(companyId)}
          />
        )}
      </Dialog>
    </Box>
  );
};

/* ───────────────────────────────────────────────────────────────────────── */

const AddPlaylistDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onPick: (p: IPlaylist) => void;
  excludeIds: Set<number>;
  canCreatePlaylist: boolean;
  onCreateNew: () => void;
  isAdding: boolean;
}> = ({ open, onClose, onPick, excludeIds, canCreatePlaylist, onCreateNew, isAdding }) => {
  const [search, setSearch] = useState("");
  const { data } = useGetPlaylistsQuery({ searchText: search });

  const playlists: IPlaylist[] = useMemo(() =>
    (Array.isArray(data?.data) ? ((data?.data as unknown) as IPlaylist[]) : [])
      .filter(p => p.is_active && !excludeIds.has(p.id)),
  [data, excludeIds]);

  // The "+ Create New Playlist" action — styled as an action, not a playlist.
  const createAction = canCreatePlaylist && (
    <Box
      onClick={() => { if (!isAdding) onCreateNew(); }}
      sx={{
        mt: 1.5, pt: 1.75,
        borderTop: `1px solid ${BORDER}`,
        display: "flex", alignItems: "center", gap: 1.25,
        px: 1.75, py: 1.5, borderRadius: 2.5,
        cursor: isAdding ? "default" : "pointer",
        opacity: isAdding ? 0.6 : 1,
        pointerEvents: isAdding ? "none" : "auto",
        transition: "background 0.15s ease",
        "&:hover": { bgcolor: alpha("#7c3aed", 0.06) },
      }}>
      <AddIcon sx={{ fontSize: 20, color: "#7c3aed" }} />
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#7c3aed" }}>
        Create New Playlist
      </Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: NAVY }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: 2,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(135deg, ${alpha("#7c3aed", 0.12)} 0%, ${alpha("#7c3aed", 0.06)} 100%)`,
          }}>
            <PlaylistPlayIcon sx={{ fontSize: 16, color: "#7c3aed" }} />
          </Box>
          Add Playlist to Group
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* 1 — Search */}
        <TextField autoFocus fullWidth size="small"
          placeholder="Search playlists"
          value={search} onChange={(e) => setSearch(e.target.value)}
          sx={{ mt: 1, mb: 2, ...signageInputSx }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }} />

        {playlists.length === 0 ? (
          /* Empty state — no playlists available to add */
          <Box sx={{ textAlign: "center", py: 4 }}>
            <PlaylistPlayIcon sx={{ fontSize: 34, color: "#cbd5e1", mb: 1 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY, mb: canCreatePlaylist ? 2 : 0 }}>
              No playlists available yet
            </Typography>
            {canCreatePlaylist && (
              <Button variant="contained" disableElevation
                disabled={isAdding}
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={onCreateNew}
                sx={{
                  borderRadius: 2.5, textTransform: "none", fontSize: 13, fontWeight: 700, px: 2.5,
                  background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
                  "&:hover": { background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)" },
                }}>
                Create Your First Playlist
              </Button>
            )}
          </Box>
        ) : (
          <Box>
            {/* 2 — Existing playlist list */}
            {playlists.map(p => (
              <Paper key={p.id} elevation={0}
                onClick={() => { if (!isAdding) onPick(p); }}
                sx={{
                  p: 1.75, mb: 1, borderRadius: 2.5,
                  border: `1px solid ${BORDER}`,
                  cursor: isAdding ? "default" : "pointer",
                  opacity: isAdding ? 0.6 : 1,
                  pointerEvents: isAdding ? "none" : "auto",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: "#7c3aed",
                    transform: "translateY(-1px)",
                    boxShadow: `0 6px 16px ${alpha("#7c3aed", 0.15)}`,
                  },
                  display: "flex", alignItems: "center", gap: 2,
                }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: 1.5,
                  bgcolor: alpha("#7c3aed", 0.12), color: "#7c3aed",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <PlaylistPlayIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
                    {p.name}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                    <Chip size="small" label={`v${p.version}`} sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: alpha(GOLD, 0.12), color: "#b58a13" }} />
                    <Chip size="small" label={p.duration_mode || "PLAY_FULL"} sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#eef2ff", color: "#4338ca" }} />
                    <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>
                      {p.items_count ?? 0} items
                    </Typography>
                  </Box>
                </Box>
                <AddIcon sx={{ color: "#7c3aed" }} />
              </Paper>
            ))}

            {/* 3 — Create New Playlist action */}
            {createAction}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}
          sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, color: "#64748b" }}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlaylistGroupEdit;
